import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import type {InsightTimeGranularityV4} from "../../models/crm-v4";
import {formatLocalDate} from "../../kit/insightsFormat";

export interface TrendBucket {
    key: string;
    /** Short form for an axis tick. */
    label: string;
    /** The whole date, for a tooltip title or a table cell where there is room for it. */
    full: string;
}

const dateFromIso = (value: string) => new Date(`${value}T00:00:00`);

/**
 * Axis and tooltip wording for one series bucket. Monthly buckets name the month, finer ones name
 * the day — a "12 Aug" tick under a month-long bar would be read as that single day.
 */
export function useBucketLabels(
    points: Array<{from: string}>,
    granularity?: InsightTimeGranularityV4,
): TrendBucket[] {
    const {i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;

    return useMemo(() => {
        // "09. Aug", the same tick `InsightsSeriesChart` writes — two charts sit side by side on the
        // Trends grid and must not date the same day two ways.
        const short = (date: Date) => formatLocalDate(granularity === "MONTH" ? "LLL yy" : "dd. LLL", date.getTime());
        const full = new Intl.DateTimeFormat(locale, granularity === "MONTH"
            ? {month: "long", year: "numeric"}
            : {dateStyle: "medium"});

        return points.map(point => {
            const date = dateFromIso(point.from);

            return {key: point.from, label: short(date), full: full.format(date)};
        });
    }, [granularity, locale, points]);
}
